import { grpc } from "@improbable-eng/grpc-web";
import { UnaryOutput } from "@improbable-eng/grpc-web/dist/typings/unary";
import { ProtobufMessage } from "@improbable-eng/grpc-web/dist/typings/message";

import AuthUtils from "@/utils/auth-utils";
import { toTitleCase } from "@/utils/common-utils";

import { Admin as Service } from "@/proto/admin/all_pb_service";
import * as ServiceProto from "@/proto/admin/all_pb";
import { Product, ProductStatus } from "@/proto/identity/identity/all_pb";

import {
  UpdateCredentialsRequest,
  IntegrationsPayload,
  UpdateIntegrationPayload,
  Integrations,
} from "@/types";
import {
  BankCodeMap,
  DestinationBankCodeMap,
} from "@/proto/b-enterprise/v1/fastcheckout/all_pb";

export default class BankIntegrationService {
  constructor(private baseURL: string, private token: string) {}

  public async getIntegrations(
    payload: IntegrationsPayload
  ): Promise<Integrations> {
    const request = new ServiceProto.GetIntegrationsRequest();
    if (payload?.country) {
      request.setCountry(payload.country);
    }
    if (payload?.bankCode) {
      request.setBankcode(payload.bankCode);
    }
    if (payload?.product) {
      request.setProduct(payload.product);
    }

    const { message, status, statusMessage }: UnaryOutput<ProtobufMessage> =
      await new Promise((resolve) => {
        grpc.unary(Service.GetIntegrations, {
          request,
          host: this.baseURL,
          onEnd: resolve,
          metadata: AuthUtils.getMetadata(this.token),
        });
      });

    if (status !== grpc.Code.OK) {
      throw new Error(
        `Error with getIntegrations: code=${status} msg=${statusMessage}`
      );
    }

    const decoded = message as ServiceProto.GetIntegrationsResponse;
    const res = decoded.toObject();

    const productsList = res.productsList.map((product) => {
      const total = product.integrationsList.length;
      const enabled = product.integrationsList.reduce((count, integration) => {
        return integration.status === ProductStatus.ENABLED ? count + 1 : count;
      }, 0);

      return {
        ...product,
        total,
        enabled,
        isNotFullyEnabled: total > enabled,
        name: toTitleCase(
          Object.keys(Product).find((key) => Product[key] === product.product)
        ),
      };
    });

    return productsList;
  }

  public async updateIntegrationStatus(
    payload: UpdateIntegrationPayload
  ): Promise<any> {
    const request = new ServiceProto.UpdateIntegrationStatusRequest();

    if (payload?.integrationId) {
      request.setIntegrationid(payload.integrationId);
    }
    if (payload?.country) {
      request.setCountry(payload.country);
    }
    if (payload?.product) {
      request.setProduct(payload.product);
    }
    if (payload?.status) {
      request.setStatus(payload.status);
    }
    if (payload?.productstatus) {
      request.setProductstatus(payload.productstatus);
    }

    request.setEmailtemplate(payload.reason);

    if (payload.reason === ServiceProto.EmailAdvisoryTemplate.CUSTOM) {
      request.setEmailtitle(payload.title);
      request.setEmailbody(payload.body);
    } else if (payload?.notes) {
      request.setNote(payload.notes);
    }

    const { message, status, statusMessage }: UnaryOutput<ProtobufMessage> =
      await new Promise((resolve) => {
        grpc.unary(Service.UpdateIntegrationStatus, {
          request,
          host: this.baseURL,
          onEnd: resolve,
          metadata: AuthUtils.getMetadata(this.token),
        });
      });

    if (status !== grpc.Code.OK) {
      throw new Error(
        `Error with updateIntegrationStatus: code=${status} msg=${statusMessage}`
      );
    }

    const decoded = message as any;
    return decoded.toObject();
  }

  public async updateFIService(
    id: string,
    payload: UpdateCredentialsRequest
  ): Promise<void> {
    const request = new ServiceProto.UpdateFIServiceRequest();
    request.setId(id);

    request.setApikey(payload.apiKey);
    request.setSenderaccountnumber(payload.senderAccountNo);
    request.setAcquiringbin(payload.acquiringBin);
    request.setMerchantbankcode(
      payload.merchantBankCode as DestinationBankCodeMap[keyof DestinationBankCodeMap]
    );
    request.setPayerbankcode(
      payload.payerBankCode as BankCodeMap[keyof BankCodeMap]
    );

    const { status, statusMessage }: UnaryOutput<ProtobufMessage> =
      await new Promise((resolve) => {
        grpc.unary(Service.UpdateFIService, {
          request,
          host: this.baseURL,
          onEnd: resolve,
          metadata: AuthUtils.getMetadata(this.token),
        });
      });

    if (status !== grpc.Code.OK) {
      throw new Error(
        `Error with UpdateFIService: code=${status} msg=${statusMessage}`
      );
    }
  }
}
